import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControlLabel,
    Grid,
    LinearProgress,
    Link,
    Paper,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowsRotate, faPlay, faStop} from '@fortawesome/pro-solid-svg-icons';
import {useSnackbar} from 'notistack';
import {format, parseISO} from 'date-fns';

import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {settingsApi} from 'Pages/Admin/Settings/settingsApi';
import {apiErrorMessage} from 'Pages/Calendar/errorText';
import OrderDetailDialog from 'Pages/Tasks/OrderDetailDialog';

import SessionDataDialog from './SessionDataDialog';
import SessionFinishDialog from './SessionFinishDialog';
import {
    SupportSession,
    SupportSessionState,
    elapsedSeconds,
    formatDuration,
} from './supportSessionTypes';

const when = (iso: string): string => format(parseISO(iso), 'dd.MM.yyyy HH:mm');

/**
 * Die Zeiterfassung am Fall.
 *
 * Der Ablauf: Der Kunde ruft an, hier wird die Uhr gestartet. Während des Gesprächs werden die
 * Daten aufgenommen und gespeichert. Beim Abschluss entsteht daraus in Labelwin, was der Fall
 * verlangt — eine Notiz, eine Aufgabe oder ein Auftrag samt Zeitbuchung.
 *
 * Es läuft höchstens **eine** Uhr: zwei würden dieselbe Arbeitszeit auf zwei Aufträge buchen.
 */
export const SupportSessions: React.FC = () => {
    const {t} = useTranslation();
    useDocumentTitle('supportSessions.title');
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [sessions, setSessions] = useState<SupportSession[]>([]);
    const [includeFinished, setIncludeFinished] = useState(false);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const [capturing, setCapturing] = useState<SupportSession | null>(null);
    const [finishing, setFinishing] = useState<SupportSession | null>(null);
    const [openOrder, setOpenOrder] = useState<string | null>(null);

    /** Tickt die Anzeige der laufenden Uhr — ohne das stünde sie zwischen zwei Abrufen still. */
    const [, setTick] = useState(0);
    const running = sessions.find((s) => s.state === SupportSessionState.Running);

    useEffect(() => {
        if (!running) return;
        const timer = window.setInterval(() => setTick((value) => value + 1), 1000);
        return () => window.clearInterval(timer);
    }, [running]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(undefined);
        try {
            const r = await http.get<SupportSession[]>('/SupportSessions/Me', {includeFinished});
            setSessions(r.data);
        } catch (e) {
            setError(apiErrorMessage(e, t('supportSessions.loadFailed')));
        } finally {
            setLoading(false);
        }
    }, [http, includeFinished, t]);

    useEffect(() => {
        void load();
    }, [load]);

    const start = async (): Promise<void> => {
        setBusy(true);
        try {
            const r = await http.post<SupportSession>('/SupportSessions', undefined, {});
            enqueueSnackbar(t('supportSessions.started'), {variant: 'success'});
            await load();
            // Direkt in die Datenaufnahme: der Anruf läuft, und die Angaben kommen jetzt.
            setCapturing(r.data);
        } catch (e) {
            enqueueSnackbar(apiErrorMessage(e, t('supportSessions.startFailed')), {variant: 'error'});
        } finally {
            setBusy(false);
        }
    };

    const stop = async (session: SupportSession): Promise<void> => {
        setBusy(true);
        try {
            await http.post<SupportSession>(`/SupportSessions/${session.id}/stop`);
            await load();
        } catch (e) {
            enqueueSnackbar(apiErrorMessage(e, t('supportSessions.saveFailed')), {variant: 'error'});
        } finally {
            setBusy(false);
        }
    };

    const resume = async (session: SupportSession): Promise<void> => {
        setBusy(true);
        try {
            await http.post<SupportSession>(`/SupportSessions/${session.id}/resume`);
            await load();
        } catch (e) {
            enqueueSnackbar(apiErrorMessage(e, t('supportSessions.saveFailed')), {variant: 'error'});
        } finally {
            setBusy(false);
        }
    };

    const discard = async (session: SupportSession): Promise<void> => {
        setBusy(true);
        try {
            await http.post<SupportSession>(`/SupportSessions/${session.id}/discard`);
            enqueueSnackbar(t('supportSessions.discarded'), {variant: 'info'});
            await load();
        } catch (e) {
            enqueueSnackbar(apiErrorMessage(e, t('supportSessions.saveFailed')), {variant: 'error'});
        } finally {
            setBusy(false);
        }
    };

    const card = (session: SupportSession): React.ReactNode => {
        const isRunning = session.state === SupportSessionState.Running;
        const isOpen = isRunning || session.state === SupportSessionState.Stopped;

        return (
            <Paper key={session.id}
                   sx={{p: 2, opacity: isOpen ? 1 : 0.75,
                       borderLeft: isRunning ? 3 : 0, borderColor: 'success.main'}}>
                <Stack direction="row" spacing={2}
                       sx={{alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 1}}>
                    <Box sx={{flexGrow: 1, minWidth: 240}}>
                        <Stack direction="row" spacing={1}
                               sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5}}>
                            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                                {session.subject?.trim() || t('supportSessions.noSubject')}
                            </Typography>
                            <Chip size="small"
                                  color={isRunning ? 'success' : 'default'}
                                  variant={isRunning ? 'filled' : 'outlined'}
                                  label={formatDuration(elapsedSeconds(session))}/>
                            {session.state === SupportSessionState.Stopped && (
                                <Chip size="small" variant="outlined"
                                      label={t('supportSessions.state.stopped')}/>
                            )}
                            {session.state === SupportSessionState.Discarded && (
                                <Chip size="small" variant="outlined"
                                      label={t('supportSessions.state.discarded')}/>
                            )}
                            {session.labelTaskId && (
                                <Chip size="small" color="info" variant="outlined"
                                      label={t('supportSessions.taskChip', {id: session.labelTaskId})}/>
                            )}
                            {session.orderNumber && (
                                <Chip size="small" color="success" variant="outlined"
                                      label={session.orderNumber}
                                      onClick={() => setOpenOrder(session.orderNumber!)}/>
                            )}
                            {session.bookedHours != null && (
                                <Chip size="small" variant="outlined"
                                      label={t('supportSessions.bookedChip', {hours: session.bookedHours})}/>
                            )}
                        </Stack>

                        {(session.customerName || session.callerName || session.callerPhone) && (
                            <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                                {[session.customerName, session.callerName].filter(Boolean).join(' · ')}
                                {session.callerPhone && <>
                                    {' · '}
                                    <Link href={`tel:${session.callerPhone.replace(/[^+0-9]/g, '')}`}
                                          underline="hover">
                                        {session.callerPhone}
                                    </Link>
                                </>}
                            </Typography>
                        )}

                        {session.notes && (
                            <Typography variant="body2" sx={{mt: 0.5, whiteSpace: 'pre-wrap'}}>
                                {session.notes}
                            </Typography>
                        )}

                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.5}}>
                            {t('supportSessions.startedAt', {when: when(session.startedAt)})}
                            {session.finishedAt &&
                                ` — ${t('supportSessions.finishedAt', {when: when(session.finishedAt)})}`}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{flexShrink: 0}}>
                        {isOpen && (
                            <Button size="small" variant="text" disabled={busy}
                                    onClick={() => setCapturing(session)}>
                                {t('supportSessions.capture')}
                            </Button>
                        )}
                        {isRunning && (
                            <Button size="small" variant="text" disabled={busy}
                                    startIcon={<FontAwesomeIcon icon={faStop}/>}
                                    onClick={() => void stop(session)}>
                                {t('supportSessions.stop')}
                            </Button>
                        )}
                        {session.state === SupportSessionState.Stopped && (
                            <Button size="small" variant="text" disabled={busy}
                                    startIcon={<FontAwesomeIcon icon={faPlay}/>}
                                    onClick={() => void resume(session)}>
                                {t('supportSessions.resume')}
                            </Button>
                        )}
                        {isOpen && (
                            <Button size="small" variant="contained" disabled={busy}
                                    onClick={() => setFinishing(session)}>
                                {t('supportSessions.finish')}
                            </Button>
                        )}
                        {isOpen && (
                            <Button size="small" variant="text" color="error" disabled={busy}
                                    onClick={() => void discard(session)}>
                                {t('supportSessions.discard')}
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Paper>
        );
    };

    return (
        <Grid size={12}>
            <Typography variant="h5" gutterBottom>{t('supportSessions.title')}</Typography>

            <Stack direction="row" spacing={1}
                   sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 1, mb: 2}}>
                {/* Nur eine Uhr je Person — läuft eine, führt der Weg über sie. */}
                <Button variant="contained" startIcon={<FontAwesomeIcon icon={faPlay}/>}
                        onClick={() => void start()} disabled={busy || running !== undefined}>
                    {t('supportSessions.start')}
                </Button>
                <Button variant="text" startIcon={<FontAwesomeIcon icon={faArrowsRotate}/>}
                        onClick={() => void load()} disabled={loading}>
                    {t('supportSessions.reload')}
                </Button>
                <Box sx={{flexGrow: 1}}/>
                <FormControlLabel
                    control={<Switch checked={includeFinished}
                                     onChange={(e) => setIncludeFinished(e.target.checked)}/>}
                    label={t('supportSessions.showFinished')}/>
            </Stack>

            {loading && <LinearProgress sx={{mb: 2}}/>}
            {error && <Alert severity="error" sx={{mb: 2}} onClose={() => setError(undefined)}>{error}</Alert>}

            {!loading && !error && sessions.length === 0 && (
                <Alert severity="info" sx={{mb: 2}}>{t('supportSessions.empty')}</Alert>
            )}

            <Stack spacing={1.5}>{sessions.map(card)}</Stack>

            <SessionDataDialog
                session={capturing}
                onClose={() => setCapturing(null)}
                onSaved={() => void load()}/>

            <SessionFinishDialog
                session={finishing}
                onClose={() => setFinishing(null)}
                onFinished={() => void load()}/>

            <OrderDetailDialog
                orderNumber={openOrder}
                onClose={() => setOpenOrder(null)}
                onChanged={() => void load()}/>
        </Grid>
    );
};

export default SupportSessions;
